import { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import {
  confirmUserAttribute,
  sendUserAttributeVerificationCode,
} from "aws-amplify/auth";
import Button from "components/Buttons";
import { gql, useMutation } from "@apollo/client";
import { UPDATE_USER } from "apollo/mutations/Client";
import ModalStore from "components/Modal/ModalStore";
import { useProfileStore } from "stores/profile/useProfileStore";
import { useModalStore } from "stores/modal/useModalStore";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "components/ui/input";

const VerifyPhoneCodeSchema = z.object({
  code: z
    .string({
      required_error: "Code is required",
    })
    .nonempty("Code is required")
});

const VerifyPhoneCodeForm = () => {
  const methods = useForm({
    resolver: zodResolver(VerifyPhoneCodeSchema),
    mode: "onBlur",
    defaultValues: {
      code: "",
    },
  });
  const close = useModalStore(s => s.close);
  const setEditPhone = useProfileStore(state => state.setEditPhone);
  const setEditAddress = useProfileStore(state => state.setEditAddress);
  const setEditFullName = useProfileStore(state => state.setEditFullName);
  const [updateUser] = useMutation(gql(UPDATE_USER));
  const [loading] = useState(false);
  const [verifyPhoneCode] = useState(true);
  const [countdown, setCountdown] = useState(120);
  const [showCountDown, setShowCountDown] = useState(true);

  const VerifyCodeSubmit = async () => {
    try {
      await confirmUserAttribute({
        userAttributeKey: "phone_number",
        confirmationCode: methods.getValues()?.code,
      });
      await updateUser({
        variables: {
          input: {
            isPhoneVerified: true,
          },
        },
        refetchQueries: ["ClientQuery"],
      });
      close();
      setEditFullName(false);
      setEditPhone(false);
      setEditAddress(false);
      ModalStore.openSimpleSuccess({
        title: "SUCCESS!",
        subtitle: "Phone verified successfully",
        shouldCloseOnOverlayClick: false,
      });
    } catch (error) {
      ModalStore.openSimpleError({
        icon: undefined,
        title: "Error",
        subtitle: error?.message,
        shouldCloseOnOverlayClick: false,
      });
    }
  };

  useEffect(() => {
    let interval;
    if (verifyPhoneCode) {
      interval = setInterval(() => {
        setCountdown((prevCountdown) =>
          prevCountdown > 0 ? prevCountdown - 1 : 0
        );
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [verifyPhoneCode]);

  useEffect(() => {
    if (countdown === 0) {
      setShowCountDown(false);
    }
  }, [countdown]);

  const handleResendCode = async () => {
    try {
      await sendUserAttributeVerificationCode({
        userAttributeKey: "phone_number",
      });
      setShowCountDown(true);
      setCountdown(120);
    } catch (error) {
      ModalStore.openSimpleError({
        icon: undefined,
        title: "Error",
        subtitle: error?.message,
        shouldCloseOnOverlayClick: false,
      });
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const renderVerifyPhoneCodeForm = () => (
    <div className="register-content">
      <div className="register-content-form mt-4">
        <Input
          name="code"
          type="number"
          label="Code"
          placeholder="Enter code"
          {...methods.register("code")}
          onInput={(e) => {
            if (e.target.value.length > 6) {
              e.target.value = e.target.value.slice(0, 6);
            }
          }}
        />
      </div>
        {methods?.formState?.errors.code && (
          <span className="text-red-500 text-sm font-montserrat mt-1">
            {methods?.formState?.errors.code?.message}
          </span>
        )}
      <div className="register-content-submit mt-[19px]">
        <Button
          type="button"
          className={`modal_form_btn ${countdown > 0 && "!bg-gray-300"}`}
          disabled={countdown > 0}
          onClick={handleResendCode}
        >
          Resend code
        </Button>
        {showCountDown && (
          <span className="timer-text font-montserrat">{formatTime(countdown)}</span>
        )}
        <Button
          whiteOutline
          type="submit"
          className="modal_form_btn"
          primary
          loading={loading}
        >
          Confirm
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(VerifyCodeSubmit)}>
          {verifyPhoneCode && renderVerifyPhoneCodeForm()}
        </form>
      </FormProvider>
    </>
  );
};

export default VerifyPhoneCodeForm;
